import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EnvelopeSimple, ArrowRight, ShieldCheck, GoogleLogo, Lock, Eye, EyeSlash, ArrowLeft } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { track } from '../lib/analytics';

function formatError(err) {
  if (!err) return "";
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  if (err?.response?.status === 0 || !err?.response) return "Network error. Please check your connection and try again.";
  if (err?.response?.status >= 500) return "Server error. Please try again later.";
  if (err?.message) return err.message;
  return "Something went wrong. Please try again.";
}

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const prefs = location.state || {};
  const googleAuth = prefs.googleAuth;

  const [mode, setMode] = useState(googleAuth ? 'signup-register' : 'login'); // login | signup | signup-otp | signup-register | forgot | reset
  const [email, setEmail] = useState(prefs.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [tempToken, setTempToken] = useState(prefs.tempToken || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [currentCity, setCurrentCity] = useState('');
  const [bio, setBio] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (prefs.googleName) {
      const parts = prefs.googleName.split(' ');
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
    }
  }, [prefs.googleName]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/browse';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // ─── LOGIN ───
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/login', { email, password });
      login(data.user, data.token);
      navigate('/browse');
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  // ─── SIGNUP: Send OTP ───
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api.post('/auth/send-otp', { email });
      setMode('signup-otp');
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  // ─── SIGNUP: Verify OTP ───
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/verify-otp', { email, code: otp });
      if (data.needs_registration) {
        setTempToken(data.temp_token);
        setMode('signup-register');
      } else {
        login(data.user, data.token);
        navigate('/browse');
      }
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  // ─── SIGNUP: Complete Registration ───
  const handleRegister = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/register', {
        temp_token: tempToken,
        first_name: firstName,
        last_name: lastName,
        dob,
        heritage_country: prefs.heritage || '',
        gender: prefs.gender || '',
        looking_for: prefs.lookingFor || '',
        age_range_min: prefs.ageRangeMin || 18,
        age_range_max: prefs.ageRangeMax || 45,
        current_city: currentCity,
        bio,
        password: newPassword,
        password_confirm: confirmPassword
      });
      login(data.user, data.token);
      track('signup_completed');
      navigate('/browse');
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  // ─── FORGOT PASSWORD ───
  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSuccess(data.message);
      setMode('reset');
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  // ─── RESET PASSWORD ───
  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      const { data } = await api.post('/auth/reset-password', { token: resetToken, password: newPassword });
      setSuccess(data.message);
      setTimeout(() => { setMode('login'); setSuccess(''); }, 2000);
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  const isLogin = mode === 'login';
  const isSignup = mode === 'signup';
  const isOtp = mode === 'signup-otp';
  const isRegister = mode === 'signup-register';
  const isForgot = mode === 'forgot';
  const isReset = mode === 'reset';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="auth-page">
      <motion.div
        key={mode}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-8 md:p-10 max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <ShieldCheck size={36} weight="light" className="text-[#D4AF37] mx-auto mb-3" />
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white font-['Outfit']">
            {isLogin && 'Welcome Back'}
            {isSignup && 'Create Account'}
            {isOtp && 'Verify Email'}
            {isRegister && 'Complete Profile'}
            {isForgot && 'Forgot Password'}
            {isReset && 'Reset Password'}
          </h2>
          <p className="text-[#9EABCE] text-sm mt-1.5">
            {isLogin && 'Log in to your YuBlink account'}
            {isSignup && 'Sign up with email verification'}
            {isOtp && `Enter the 6-digit code sent to ${email}`}
            {isRegister && 'Tell us about yourself'}
            {isForgot && 'Enter your email to receive a reset link'}
            {isReset && 'Enter the reset code and your new password'}
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-5 text-sm text-red-400 text-center" data-testid="auth-error">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 mb-5 text-sm text-green-400 text-center" data-testid="auth-success">
            {success}
          </div>
        )}

        {/* ═══════ LOGIN MODE ═══════ */}
        {isLogin && (
          <div className="space-y-5">
            <button type="button" onClick={handleGoogleLogin} data-testid="auth-google-btn"
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-white text-[#1A1A2E] font-bold text-sm hover:bg-gray-100 transition-all shadow-lg">
              <GoogleLogo size={20} weight="bold" /> Continue with Google
            </button>

            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-[#9EABCE]/60 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-[#9EABCE] text-sm mb-1.5 block">Email</Label>
                <div className="relative">
                  <EnvelopeSimple size={18} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required
                    data-testid="auth-email-input" className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-12" />
                </div>
              </div>
              <div>
                <Label className="text-[#9EABCE] text-sm mb-1.5 block">Password</Label>
                <div className="relative">
                  <Lock size={18} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                  <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required
                    data-testid="auth-password-input" className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-12" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9EABCE] hover:text-white">
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => { setMode('forgot'); setError(''); }} data-testid="forgot-password-link"
                  className="text-xs text-[#D4AF37] hover:text-[#F0C84A] transition-colors">
                  Forgot Password?
                </button>
              </div>
              <button type="submit" disabled={loading || !email || !password} data-testid="auth-login-btn"
                className="w-full btn-gold py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? 'Logging in...' : 'Log In'} <ArrowRight size={18} />
              </button>
            </form>

            <p className="text-center text-sm text-[#9EABCE]">
              Don't have an account?{' '}
              <button onClick={() => { setMode('signup'); setError(''); }} data-testid="switch-to-signup"
                className="text-[#D4AF37] hover:text-[#F0C84A] font-semibold transition-colors">
                Sign Up
              </button>
            </p>
          </div>
        )}

        {/* ═══════ SIGNUP MODE ═══════ */}
        {isSignup && (
          <div className="space-y-5">
            <button type="button" onClick={handleGoogleLogin} data-testid="auth-google-btn"
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-white text-[#1A1A2E] font-bold text-sm hover:bg-gray-100 transition-all shadow-lg">
              <GoogleLogo size={20} weight="bold" /> Continue with Google
            </button>

            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-[#9EABCE]/60 uppercase tracking-wider">or sign up with email</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <Label className="text-[#9EABCE] text-sm mb-1.5 block">Email Address</Label>
                <div className="relative">
                  <EnvelopeSimple size={18} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required
                    data-testid="auth-email-input" className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-12" />
                </div>
              </div>
              <button type="submit" disabled={loading || !email} data-testid="auth-send-otp-btn"
                className="w-full btn-gold py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? 'Sending...' : 'Send Verification Code'} <ArrowRight size={18} />
              </button>
            </form>

            <p className="text-center text-sm text-[#9EABCE]">
              Already have an account?{' '}
              <button onClick={() => { setMode('login'); setError(''); }} data-testid="switch-to-login"
                className="text-[#D4AF37] hover:text-[#F0C84A] font-semibold transition-colors">
                Log In
              </button>
            </p>
          </div>
        )}

        {/* ═══════ OTP VERIFY MODE ═══════ */}
        {isOtp && (
          <form onSubmit={handleVerifyOTP} className="space-y-5">
            <div>
              <Label className="text-[#9EABCE] text-sm mb-1.5 block">Verification Code</Label>
              <Input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6}
                data-testid="auth-otp-input" className="bg-white/5 border-white/10 text-white text-center text-2xl tracking-[0.3em] font-bold placeholder:text-[#9EABCE]/30 focus:border-[#D4AF37] rounded-xl h-14" />
            </div>
            <button type="submit" disabled={loading || otp.length !== 6} data-testid="auth-verify-otp-btn"
              className="w-full btn-gold py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? 'Verifying...' : 'Verify & Continue'} <ArrowRight size={18} />
            </button>
            <button type="button" onClick={() => { setMode('signup'); setOtp(''); setError(''); }}
              className="w-full text-sm text-[#9EABCE] hover:text-[#D4AF37] transition-colors">
              Use a different email
            </button>
          </form>
        )}

        {/* ═══════ REGISTRATION FORM ═══════ */}
        {isRegister && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#9EABCE] text-xs mb-1 block">First Name</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required
                  data-testid="auth-firstname-input" className="bg-white/5 border-white/10 text-white focus:border-[#D4AF37] rounded-xl h-11" />
              </div>
              <div>
                <Label className="text-[#9EABCE] text-xs mb-1 block">Last Name</Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required
                  data-testid="auth-lastname-input" className="bg-white/5 border-white/10 text-white focus:border-[#D4AF37] rounded-xl h-11" />
              </div>
            </div>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block">Date of Birth</Label>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required
                data-testid="auth-dob-input" className="bg-white/5 border-white/10 text-white focus:border-[#D4AF37] rounded-xl h-11 [color-scheme:dark]" />
            </div>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block">Current City</Label>
              <Input value={currentCity} onChange={(e) => setCurrentCity(e.target.value)} placeholder="e.g., Berlin, Vienna..."
                data-testid="auth-city-input" className="bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-11" />
            </div>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block">About You</Label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Write something about yourself..." rows={2}
                data-testid="auth-bio-input" className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#D4AF37]" />
            </div>
            <div className="pt-2 border-t border-white/5">
              <Label className="text-[#9EABCE] text-xs mb-1 block">Set Password <span className="text-red-400">*</span></Label>
              <div className="relative">
                <Lock size={16} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <Input type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                  placeholder="At least 6 characters" data-testid="auth-set-password-input"
                  className="pl-9 pr-10 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-11" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9EABCE] hover:text-white">
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block">Confirm Password <span className="text-red-400">*</span></Label>
              <div className="relative">
                <Lock size={16} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <Input type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                  placeholder="Re-enter your password" data-testid="auth-confirm-password-input"
                  className={`pl-9 bg-white/5 border text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-11 ${
                    confirmPassword && newPassword !== confirmPassword ? 'border-red-500/50' : 'border-white/10'
                  }`} />
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1" data-testid="password-mismatch-warning">Passwords don't match</p>
              )}
              <p className="text-xs text-[#9EABCE]/50 mt-1">You'll use this email + password to log in next time.</p>
            </div>
            <button type="submit"
              disabled={loading || !firstName || !lastName || !dob || newPassword.length < 6 || newPassword !== confirmPassword}
              data-testid="auth-register-btn"
              className="w-full btn-gold py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? 'Creating Account...' : 'Join YuBlink'} <ArrowRight size={18} />
            </button>
          </form>
        )}

        {/* ═══════ FORGOT PASSWORD MODE ═══════ */}
        {isForgot && (
          <form onSubmit={handleForgot} className="space-y-5">
            <div>
              <Label className="text-[#9EABCE] text-sm mb-1.5 block">Email Address</Label>
              <div className="relative">
                <EnvelopeSimple size={18} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required
                  data-testid="auth-forgot-email" className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-12" />
              </div>
            </div>
            <button type="submit" disabled={loading || !email} data-testid="auth-forgot-btn"
              className="w-full btn-gold py-3 rounded-xl font-bold disabled:opacity-50">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
              className="w-full flex items-center justify-center gap-2 text-sm text-[#9EABCE] hover:text-[#D4AF37] transition-colors">
              <ArrowLeft size={16} /> Back to Login
            </button>
          </form>
        )}

        {/* ═══════ RESET PASSWORD MODE ═══════ */}
        {isReset && (
          <form onSubmit={handleReset} className="space-y-5">
            <div>
              <Label className="text-[#9EABCE] text-sm mb-1.5 block">Reset Code (from email)</Label>
              <Input value={resetToken} onChange={(e) => setResetToken(e.target.value)} placeholder="Paste the reset code" required
                data-testid="auth-reset-token" className="bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-12 font-mono text-sm" />
            </div>
            <div>
              <Label className="text-[#9EABCE] text-sm mb-1.5 block">New Password (min 6 characters)</Label>
              <div className="relative">
                <Lock size={18} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <Input type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" required
                  data-testid="auth-reset-password" className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 focus:border-[#D4AF37] rounded-xl h-12" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9EABCE] hover:text-white">
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading || !resetToken || newPassword.length < 6} data-testid="auth-reset-btn"
              className="w-full btn-gold py-3 rounded-xl font-bold disabled:opacity-50">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
              className="w-full flex items-center justify-center gap-2 text-sm text-[#9EABCE] hover:text-[#D4AF37] transition-colors">
              <ArrowLeft size={16} /> Back to Login
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
